/**
 * What an invitee's device keeps of the books shared with them (part 38c, principles GP1, GP4; table CC): the invite
 * link's pair waiting to be claimed, each account's keyring key and person keys, each claim's settings, its role keys and
 * notes, and each item's copy exactly as the relay served it (sealed, with its key's wrap), opened only to be shown. A
 * database of its own, `cockatrice-shared`, beside the book stores and never inside them (no book store's layout
 * changes). Keys are `account`, `account invite` and `account invite item`, so one account's whole cache is one key range
 * (logout and IN11 drop it whole). Every value is read at the boundary; one that doesn't read is as good as absent.
 */
import { readShareNote } from '@cockatrice/core';
import type { ShareNote } from '@cockatrice/core';
import { INVITE_KEY_BYTES } from '@cockatrice/crypto';
import type { InviteNotes, InviteRole, KeyGroup } from '@cockatrice/storage';
import { INVITE_NOTES, INVITE_ROLES, KEY_GROUPS } from '@cockatrice/storage';
import type { WireNoteGroup } from '../share/invite-wire.ts';

/** GP1: an invite link's pair on this device until claimed: the claim secret and the person key, and when it was opened. */
export interface PendingInvite {
  readonly claim: Uint8Array<ArrayBuffer>;
  readonly key: Uint8Array<ArrayBuffer>;
  readonly at: number;
}

/** GP2: the keyring as this device holds it once opened: its ID and key. */
export interface HeldKeyring {
  readonly id: Uint8Array<ArrayBuffer>;
  readonly key: Uint8Array<ArrayBuffer>;
}

/** A role key opened (38e's DN2): its group, epoch and key. */
export interface HeldRoleKey {
  readonly group: KeyGroup;
  readonly epoch: number;
  readonly key: Uint8Array<ArrayBuffer>;
}

/** A note opened (table NV): its relay ID, order, group, thread, author (null: the writer), key epoch, time and what it says. */
export interface HeldNote {
  readonly id: string;
  readonly seq: number;
  readonly group: WireNoteGroup;
  readonly thread: string | null;
  readonly author: string | null;
  readonly epoch: number;
  readonly at: number;
  readonly note: ShareNote;
}

/** A claim as cached (CC2): its settings, items, person key, role keys, notes and their cursor, and its last check. */
export interface CachedClaim {
  readonly invite: string;
  readonly share: string;
  readonly role: InviteRole;
  readonly notes: InviteNotes;
  readonly export: boolean;
  readonly endsAt: number | null;
  /** The items the relay says the claim holds (its chapters and its manifest). */
  readonly chapters: readonly string[];
  /** The person key, from the keyring; null while its entry isn't stored (CC11). */
  readonly person: Uint8Array<ArrayBuffer> | null;
  readonly roleKeys: readonly HeldRoleKey[];
  readonly cursor: number;
  readonly held: readonly HeldNote[];
  /**
   * Deep-fix r1, table NC: what the notes held were pulled under (the role, the notes level, the role keys by group and
   * epoch): when it changes, the cursor starts again, so no note is skipped for a group or key not readable before.
   */
  readonly reads: string;
  readonly checkedAt: number;
}

/** An item's copy as served (GP5): its version, sealed bytes and its key's wrap for this invite. */
export interface CachedItem {
  readonly item: string;
  readonly version: Uint8Array<ArrayBuffer>;
  readonly sealed: Uint8Array<ArrayBuffer>;
  readonly wrap: Uint8Array<ArrayBuffer>;
}

/** The invitee's storage on this device. */
export interface SharedStore {
  pending(): Promise<PendingInvite | null>;
  /** JL5: keeps `pending`, replacing any waiting. */
  keepPending(pending: PendingInvite): Promise<void>;
  dropPending(): Promise<void>;
  keyring(account: string): Promise<HeldKeyring | null>;
  keepKeyring(account: string, keyring: HeldKeyring): Promise<void>;
  claims(account: string): Promise<readonly CachedClaim[]>;
  keepClaim(account: string, claim: CachedClaim): Promise<void>;
  items(account: string, invite: string): Promise<readonly CachedItem[]>;
  keepItem(account: string, invite: string, item: CachedItem): Promise<void>;
  dropItems(account: string, invite: string, items: readonly string[]): Promise<void>;
  /** CC1, CC9: claims `invites` dropped with their items, in one write. */
  dropClaims(account: string, invites: readonly string[]): Promise<void>;
  /** CC10, KG3: everything of `account`'s dropped (its keyring, claims and items), in one write. */
  clear(account: string): Promise<void>;
  /** Table AS: every account holding a keyring or a claim here (so those no longer logged in are cleared). */
  accounts(): Promise<readonly string[]>;
  close(): void;
}

function bytesOf(value: unknown, length?: number): Uint8Array<ArrayBuffer> | null {
  if (!(value instanceof Uint8Array) || (length !== undefined && value.length !== length) || value.length === 0) return null;
  return new Uint8Array(value);
}

function get(value: unknown, field: string): unknown {
  return typeof value === 'object' && value !== null ? Reflect.get(value, field) : undefined;
}

function count(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

function textOrNull(value: unknown): string | null | undefined {
  return value === null ? null : (text(value) ?? undefined);
}

/** A pending invite as kept, or null. */
export function readPending(value: unknown): PendingInvite | null {
  const claim = bytesOf(get(value, 'claim'), 16);
  const key = bytesOf(get(value, 'key'), INVITE_KEY_BYTES);
  const at = count(get(value, 'at'));
  return claim === null || key === null || at === null ? null : { claim, key, at };
}

/** A held keyring as kept, or null. */
export function readHeldKeyring(value: unknown): HeldKeyring | null {
  const id = bytesOf(get(value, 'id'), 16);
  const key = bytesOf(get(value, 'key'), 32);
  return id === null || key === null ? null : { id, key };
}

function roleKeyOf(value: unknown): HeldRoleKey | null {
  const group = KEY_GROUPS.find((known) => known === get(value, 'group'));
  const epoch = count(get(value, 'epoch'));
  const key = bytesOf(get(value, 'key'), 32);
  return group === undefined || epoch === null || key === null ? null : { group, epoch, key };
}

const GROUPS: readonly WireNoteGroup[] = [...KEY_GROUPS, 'person'];

function heldNoteOf(value: unknown): HeldNote | null {
  const id = text(get(value, 'id'));
  const seq = count(get(value, 'seq'));
  const group = GROUPS.find((known) => known === get(value, 'group'));
  const thread = textOrNull(get(value, 'thread'));
  const author = textOrNull(get(value, 'author'));
  const epoch = count(get(value, 'epoch'));
  const at = count(get(value, 'at'));
  const raw = get(value, 'note');
  // The note is kept as its item's bytes and read again strictly (decision 59: plain text, bounded).
  const note = raw instanceof Uint8Array ? readShareNote(raw) : null;
  if (id === null || seq === null || group === undefined || thread === undefined || author === undefined || epoch === null || at === null || note === null) return null;
  return { id, seq, group, thread, author, epoch, at, note };
}

function listOf<T>(value: unknown, read: (entry: unknown) => T | null): T[] | null {
  if (!Array.isArray(value)) return null;
  const read_ = value.map(read);
  return read_.every((entry): entry is T => entry !== null) ? read_ : null;
}

/** A cached claim as kept, or null (dropped then, as absent: the next check fetches it again). */
export function readCachedClaim(value: unknown): CachedClaim | null {
  const invite = text(get(value, 'invite'));
  const share = text(get(value, 'share'));
  const role = INVITE_ROLES.find((known) => known === get(value, 'role'));
  const notes = INVITE_NOTES.find((known) => known === get(value, 'notes'));
  const exported = get(value, 'export');
  const ends = get(value, 'endsAt');
  const endsAt = ends === null ? null : count(ends);
  const chapters = listOf(get(value, 'chapters'), text);
  const rawPerson = get(value, 'person');
  const person = rawPerson === null ? null : bytesOf(rawPerson, INVITE_KEY_BYTES);
  const roleKeys = listOf(get(value, 'roleKeys'), roleKeyOf);
  const cursor = count(get(value, 'cursor'));
  const held = listOf(get(value, 'held'), heldNoteOf);
  const checkedAt = count(get(value, 'checkedAt'));
  const raw = get(value, 'reads');
  const reads = typeof raw === 'string' ? raw : null;
  if (
    invite === null ||
    share === null ||
    role === undefined ||
    notes === undefined ||
    typeof exported !== 'boolean' ||
    (ends !== null && endsAt === null) ||
    chapters === null ||
    (rawPerson !== null && person === null) ||
    roleKeys === null ||
    cursor === null ||
    held === null ||
    checkedAt === null ||
    reads === null
  ) {
    return null;
  }
  return { invite, share, role, notes, export: exported, endsAt, chapters, person, roleKeys, cursor, held, reads, checkedAt };
}

/** A cached item as kept, or null. */
export function readCachedItem(value: unknown): CachedItem | null {
  const item = text(get(value, 'item'));
  const version = bytesOf(get(value, 'version'), 16);
  const sealed = bytesOf(get(value, 'sealed'));
  const wrap = bytesOf(get(value, 'wrap'));
  return item === null || version === null || sealed === null || wrap === null ? null : { item, version, sealed, wrap };
}

/** A claim as it is put: its notes as their items' bytes (read again by {@link readCachedClaim}). */
export function claimValue(claim: CachedClaim, encode: (note: ShareNote) => Uint8Array): unknown {
  return { ...claim, held: claim.held.map((held) => ({ ...held, note: encode(held.note) })) };
}

/** The keys of `account`'s values, and of one invite's: each a prefix, then a space and the next part. */
export function claimKey(account: string, invite: string): string {
  return `${account} ${invite}`;
}

/** An item's key. */
export function itemKey(account: string, invite: string, item: string): string {
  return `${account} ${invite} ${item}`;
}

/** Every key that starts with `prefix` and a space (a key range's bounds: the space, then the highest code unit). */
export function under(prefix: string): readonly [string, string] {
  return [`${prefix} `, `${prefix} ￿`];
}
